import React, { useEffect, useState, useCallback } from "react";
import {
    Search,
    Download,
    Eye,
    Edit,
    Trash2,
    Copy,
    Power,
    MoreHorizontal
} from "lucide-react";

const slugify = (text) => {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // Supprime les accents
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, '-') // Remplace les espaces par des tirets
        .replace(/[^\w-]+/g, '') // Supprime les caractères spéciaux
        .replace(/--+/g, '-'); // Évite les tirets consécutifs
};

/**
 * Mappe le type de table (Int) vers son nom pour l'URL
 */
const getTableTypeString = (type) => {
    const t = parseInt(type);
    if (t === 1) return 'table';
    if (t === 2) return 'takeout';
    if (t === 3) return 'client';
    return 'table';
};
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { base_api_url, base_ui_url } from "../../../environment.jsx";
import SessionControl from "../../components/SessionControl/sessioncontrol";
import CheckRestauSelected from "../../components/SessionControl/checkrestauselected";
import { all_routes } from "../../../Router/all_routes";
import AddTables from "../../../core/modals/tables/addtable";
import QRNav from "../../components/QRNav/QRNav";
import EditTableModal from "../../../core/modals/tables/edittable";
import './Tables.css';

/**
 * Composant TablesNew - Refonte de la page de gestion des tables.
 * Permet de visualiser, créer et gérer les QR codes des tables du restaurant.
 */
const TablesNew = () => {
    const MySwal = withReactContent(Swal);
    const route = all_routes;

    // États locaux
    const [tables, setTables] = useState([]);
    const [chargement, setChargement] = useState(false);
    const [recherche, setRecherche] = useState("");
    const [filtreStatut, setFiltreStatut] = useState("Toutes");
    const [activeTab, setActiveTab] = useState("tables"); // 'tables' | 'clients'
    const [utilisateur] = useState(JSON.parse(localStorage.getItem("user")) || null);
    const [showQRDrawer, setShowQRDrawer] = useState(false);
    const [selectedTableForQR, setSelectedTableForQR] = useState(null);
    const [selectedTables, setSelectedTables] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);

    // Modales
    const [showAddTableModal, setShowAddTableModal] = useState(false);

    /**
     * Charge les tables depuis l'API.
     */
    const recupererTables = useCallback(async () => {
        setChargement(true);
        const token = utilisateur?.access_token;
        const restoId = utilisateur?.restoId;

        try {
            const url = `${base_api_url}api/table/${restoId}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.status === 200 || response.status === 201) {
                const tablesData = response.data.table || [];
                console.log("Tables récupérées :", tablesData);
                setTables(tablesData);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération des tables:", error);
        } finally {
            setChargement(false);
        }
    }, [utilisateur?.access_token, utilisateur?.restoId]);


    useEffect(() => {
        recupererTables();
    }, [recupererTables]);

    /**
     * Gère le téléchargement d'un QR code individuel.
     */
    const telechargerQRCode = async (table) => {
        try {
            const freshUser = JSON.parse(localStorage.getItem("user") || "{}");
            const freshRestaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
            const restaurantName = freshRestaurant?.restaurant?.nom || freshRestaurant?.Restaurant?.nom || freshUser?.restoNom || freshUser?.nom_restaurant || 'restaurant';
            const restaurantSlug = slugify(restaurantName);
            const typeStr = getTableTypeString(table.type_table);
            const qrData = `${base_ui_url}${restaurantSlug}/${typeStr}/homemenu?r=${utilisateur?.restoId}&t=${table.code_qr}`;
            const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(qrData)}`;

            const response = await fetch(qrApiUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Table_${table.numero_table}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Erreur téléchargement QR:", error);
        }
    };

    /**
     * Télécharge tous les QR codes avec un délai pour ne pas saturer.
     */
    const telechargerTout = async () => {
        if (tables.length === 0) return;

        MySwal.fire({
            // title: 'Préparation...',
            text: 'Vos QR codes vont être téléchargés un par un.',
            allowOutsideClick: false,
            didOpen: () => MySwal.showLoading()
        });

        for (let i = 0; i < tables.length; i++) {
            await telechargerQRCode(tables[i]);
            if (i < tables.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        MySwal.close();
    };

    /**
     * Copie le QR code dans le presse-papier.
     */
    const copierQRCode = async (table) => {
        try {
            const freshUser = JSON.parse(localStorage.getItem("user") || "{}");
            const freshRestaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
            const restaurantName = freshRestaurant?.restaurant?.nom || freshRestaurant?.Restaurant?.nom || freshUser?.restoNom || freshUser?.nom_restaurant || 'restaurant';
            const restaurantSlug = slugify(restaurantName);
            const typeStr = getTableTypeString(table.type_table);
            const qrData = `${base_ui_url}${restaurantSlug}/${typeStr}/homemenu?r=${utilisateur?.restoId}&t=${table.code_qr}`;

            await navigator.clipboard.writeText(qrData);

            MySwal.fire({
                icon: "success",
                text: "Lien du QR Code copié !",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
                customClass: {
                    popup: "pixel-toast",
                },
            });
        } catch (error) {
            console.error("Erreur lors de la copie du lien:", error);
            MySwal.fire({
                icon: "error",
                text: "Impossible de copier le lien.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
            });
        }
    };



    // Filtrage des données selon l'onglet actif et le statut
    const typeTab = activeTab === 'tables' ? 1 : activeTab === 'vad' ? 2 : 3;
    const donneesFiltrees = tables.filter(item => {
        if (item.type_table !== typeTab) return false;

        const matchesSearch = item.numero_table.toString().includes(recherche) ||
            item.code_qr.toLowerCase().includes(recherche.toLowerCase());

        if (filtreStatut === "Toutes") return matchesSearch;
        if (filtreStatut === "Actives") return matchesSearch && item.statut?.toLowerCase() === "actif";
        if (filtreStatut === "Inactives") return matchesSearch && item.statut?.toLowerCase() === "inactif";

        return matchesSearch;
    }).map((item, index) => {
        const restoId = utilisateur?.restoId;
        const freshUser = JSON.parse(localStorage.getItem("user") || "{}");
        const freshRestaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
        const restaurantName = freshRestaurant?.restaurant?.nom || freshRestaurant?.Restaurant?.nom || freshUser?.restoNom || freshUser?.nom_restaurant || 'restaurant';
        const restaurantSlug = slugify(restaurantName);
        const typeStr = getTableTypeString(item.type_table);
        return {
            ...item,
            key: index + 1,
            id: item.id_table,
            url: `${base_ui_url}${restaurantSlug}/${typeStr}/homemenu?r=${restoId}&t=${item.code_qr}`
        };
    });

    /**
     * Supprime une table avec confirmation SweetAlert.
     */

    const emptyMessage = activeTab === 'tables'
        ? 'Aucune table configurée pour le moment.'
        : activeTab === 'vad'
        ? 'Aucune vente à distance configurée pour le moment.'
        : 'Aucun QR Client configuré pour le moment.';

    const pageSubtitle = activeTab === 'tables'
        ? 'Centralisation du service en salle'
        : activeTab === 'vad'
        ? 'Livraison & Evènements'
        : 'Gestion des clients';

    return (
        <div className="page-wrapper tables-page">
            <SessionControl storageKey="user" propsup="email" redirectTo={route.signin} />
            <CheckRestauSelected storageKey="user" redirectTo={route.Restaurants} />

            <div>
                {/* EN-TÊTE */}
                <QRNav
                    activeTabId={activeTab}
                    subtitle={pageSubtitle}
                    addButtonText="Ajouter"
                    onAddClick={() => setShowAddTableModal(true)}
                    onTabChange={(tabId) => { setActiveTab(tabId); setRecherche(''); setFiltreStatut('Toutes'); }}
                />

                {/* BARRE UNIQUE : RECHERCHE + FILTRES + TÉLÉCHARGER */}
                <div className="tables-toolbar">
                    <div className="search-wrapper-tables">
                        <Search className="search-icon-tables" size={18} />
                        <input
                            type="text"
                            className="search-input-tables"
                            placeholder="Rechercher une table..."
                            value={recherche}
                            onChange={(e) => setRecherche(e.target.value)}
                        />
                    </div>
                    <div className="status-pills">
                        {['Toutes', 'Actives', 'Inactives'].map(s => (
                            <button
                                key={s}
                                className={`status-pill ${filtreStatut === s ? 'active' : ''}`}
                                onClick={() => setFiltreStatut(s)}
                            >{s}</button>
                        ))}
                    </div>

                    {/* Bulk actions when items are selected */}
                    {selectedTables.length > 0 ? (
                        <div className="bulk-actions-bar">
                            <button className="btn-bulk btn-disable" onClick={() => {
                                // Placeholder for bulk disable
                            }}>
                                <Power size={16} />
                                Désactiver ({selectedTables.length})
                            </button>
                            <button className="btn-bulk btn-download-bulk" onClick={async () => {
                                const selected = donneesFiltrees.filter(t => selectedTables.includes(t.id_table));
                                for (let i = 0; i < selected.length; i++) {
                                    await telechargerQRCode(selected[i]);
                                    if (i < selected.length - 1) await new Promise(r => setTimeout(r, 500));
                                }
                            }}>
                                <Download size={16} />
                                Télécharger ({selectedTables.length})
                            </button>
                            <button className="btn-bulk btn-delete" onClick={async () => {
                                const result = await MySwal.fire({
                                    title: 'Supprimer ?',
                                    text: `Voulez-vous supprimer ${selectedTables.length} table(s) ?`,
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#ef4444',
                                    cancelButtonText: 'Annuler',
                                    confirmButtonText: 'Supprimer',
                                });
                                if (!result.isConfirmed) return;
                                const token = utilisateur?.access_token;
                                let successCount = 0;
                                const selectedTableObjects = donneesFiltrees.filter(t => selectedTables.includes(t.id_table));
                                for (const table of selectedTableObjects) {
                                    try {
                                        await axios.put(`${base_api_url}api/tables/${table.code_qr}`,{ statut: "inactif" }, {
                                            headers: { Authorization: `Bearer ${token}` }
                                        });
                                        successCount++;
                                    } catch (e) { 
                                        console.error(e);
                                        console.error(`Erreur suppression table ${table.numero_table}:`, e);
                                     }
                                }
                                setSelectedTables([]);
                                recupererTables();
                                if (successCount > 0) {
                                    MySwal.fire({
                                        icon: "success",
                                        text: `${successCount} table(s) supprimer(s) avec succès !`,
                                        toast: true,
                                        position: "bottom-end",
                                        showConfirmButton: false,
                                        timer: 2000,
                                    });
                                }
                            }}>
                                <Trash2 size={16} />
                                Supprimer ({selectedTables.length})
                            </button>
                        </div>
                    ) : (
                        <button className="btn-download-all" onClick={telechargerTout}>
                            <Download size={16} />
                            Tout télécharger
                        </button>
                    )}
                </div>

                <div className="qr-table-list">
                    {/* Header */}
                    <div className={`qr-table-header ${activeTab !== 'tables' ? 'no-capacity' : ''}`}>
                        <div className="qr-table-header-left">
                            <label className="qr-checkbox-wrapper">
                                <input
                                    type="checkbox"
                                    className="qr-checkbox"
                                    checked={donneesFiltrees.length > 0 && selectedTables.length === donneesFiltrees.length}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedTables(donneesFiltrees.map(t => t.id_table));
                                        } else {
                                            setSelectedTables([]);
                                        }
                                    }}
                                />
                            </label>
                            <span className="qr-table-header-cell">
                                {activeTab === 'tables' ? 'N°' : 'NOM'}
                            </span>
                        </div>
                        {activeTab === 'tables' && <span className="qr-table-header-cell">CAPACITÉ</span>}
                        <span className="qr-table-header-cell status-header">STATUT</span>
                        <span className="qr-table-header-cell qr-table-header-right">ACTIONS</span>
                    </div>

                    {/* Chargement */}
                    {chargement && <p className="vad-loading">Chargement…</p>}

                    {/* État vide illustré */}
                    {!chargement && donneesFiltrees.length === 0 && (
                        <div className="qr-empty-state">
                            <div className="qr-empty-icon">
                                <Search size={24} color="#9ca3af" />
                            </div>
                            <p className="qr-empty-text">{emptyMessage}</p>
                            <button className="qr-empty-btn" onClick={() => setShowAddTableModal(true)}>Ajouter</button>
                        </div>
                    )}

                    {/* Rows */}
                    {donneesFiltrees.map((record) => {
                        const isSelected = selectedTables.includes(record.id_table);
                        const isActif = record.statut?.toLowerCase() === 'actif';
                        return (
                            <div key={record.id_table} className={`qr-table-row ${isSelected ? 'selected' : ''} ${activeTab !== 'tables' ? 'no-capacity' : ''}`}>
                                <div className="qr-table-row-left">
                                    <label className="qr-checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            className="qr-checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedTables(prev => [...prev, record.id_table]);
                                                } else {
                                                    setSelectedTables(prev => prev.filter(id => id !== record.id_table));
                                                }
                                            }}
                                        />
                                    </label>
                                    <span className="qr-table-row-name">
                                        {activeTab === 'tables' 
                                            ? String(record.numero_table).padStart(2, '0') 
                                            : (activeTab === 'vad' && !String(record.numero_table).toUpperCase().startsWith('VAD')
                                                ? `VAD ${record.numero_table}`
                                                : record.numero_table)}
                                    </span>
                                </div>
                                {activeTab === 'tables' && <span className="qr-table-row-capacity">{record.capacite || 4}</span>}
                                <div className="qr-table-row-status">
                                    <span className={isActif ? 'status-badge-actif' : 'status-badge-inactif'}>
                                        {isActif ? 'Actif' : 'Inactif'}
                                    </span>
                                </div>
                                <div className="qr-table-row-actions">
                                    <button className="btn-table-action btn-view" title="Voir le QR code"
                                        onClick={() => { setSelectedTableForQR(record); setShowQRDrawer(true); }}>
                                        <Eye size={16} />
                                    </button>
                                    <button className="btn-table-action btn-download" title="Télécharger"
                                        onClick={() => telechargerQRCode(record)}>
                                        <Download size={16} />
                                    </button>
                                    <button className="btn-table-action btn-edit" title="Modifier"
                                        onClick={() => {
                                                setSelectedTable(record);
                                                setShowEditModal(true);
                                            }}>
                                        <Edit size={16} />
                                    </button>
                                    <div className="dropdown-actions-container">
                                        <button className="btn-table-action btn-more" title="Plus">
                                            <MoreHorizontal size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* MODALE AJOUt */}
                {showAddTableModal && (
                    <AddTables
                        show={showAddTableModal}
                        onClose={() => setShowAddTableModal(false)}
                        onFormSubmit={() => {
                            setShowAddTableModal(false);
                            recupererTables();
                        }}
                        defaultType={activeTab === 'tables' ? 1 : activeTab === 'vad' ? 2 : 3}
                    />
                )}
                  {/* MODALE MODIF */}
                {showEditModal && selectedTable && (
                    <EditTableModal
                        show={showEditModal}
                        TableData={selectedTable}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedTable(null);
                        }}
                        onFormSubmit={() => {
                            setShowEditModal(false);
                            setSelectedTable(null);
                            recupererTables(); // recharge la liste
                        }}
                    />
                )}

                {/* MODAL QR CODE CENTRÉ */}
                {showQRDrawer && selectedTableForQR && (
                    <>
                        <div onClick={() => setShowQRDrawer(false)}
                            style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.4)', backdropFilter:'blur(4px)', zIndex:1999 }} />
                        <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'460px', maxWidth:'94vw', background:'#fff', borderRadius:'20px', boxShadow:'0 20px 60px rgba(0,0,0,0.18)', zIndex:2000, overflowY:'auto', maxHeight:'90vh' }}>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #f3f4f6', padding:'22px 24px 14px 24px' }}>
                                <h4 style={{ margin:0, fontSize:'17px', fontWeight:700, color:'#111827' }}>QR Code — TABLE-{String(selectedTableForQR.numero_table).padStart(2,'0')}</h4>
                                <button style={{ fontSize:22, background:'none', border:'none', cursor:'pointer', color:'#9ca3af', lineHeight:1, padding:0 }} onClick={() => setShowQRDrawer(false)}>×</button>
                            </div>
                            <div style={{ padding:'0 24px 24px 24px' }}>
                                <div style={{ margin:'16px 0 8px 0', fontWeight:500, fontSize:14, color:'#374151' }}>
                                    Statut {selectedTableForQR.statut && (
                                        <span style={{ marginLeft:8, padding:'2px 12px', borderRadius:12, background: selectedTableForQR.statut.toLowerCase()==='actif'?'#22c55e':'#e5e7eb', color: selectedTableForQR.statut.toLowerCase()==='actif'?'#fff':'#374151', fontSize:12 }}>
                                            {selectedTableForQR.statut.charAt(0).toUpperCase()+selectedTableForQR.statut.slice(1)}
                                        </span>
                                    )}
                                </div>
                                <div style={{ border:'1px dashed #e5e7eb', borderRadius:12, padding:20, margin:'14px 0', textAlign:'center', background:'#fafbfc' }}>
                                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(selectedTableForQR.url)}`}
                                        alt={`QR Code Table ${selectedTableForQR.numero_table}`}
                                        className="img-fluid border rounded"
                                        style={{ maxWidth:'260px', height:'auto', margin:'0 auto' }} />
                                </div>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                                    <div style={{ color:'#6b7280', fontSize:14 }}>Restaurant</div>
                                    <div style={{ fontWeight:500, fontSize:14 }}>
                                        {(() => {
                                            const freshUser = JSON.parse(localStorage.getItem("user") || "{}");
                                            const freshRestaurant = JSON.parse(localStorage.getItem("restaurant") || "{}");
                                            return freshRestaurant?.restaurant?.nom || freshRestaurant?.Restaurant?.nom || freshUser?.restoNom || freshUser?.nom_restaurant || '-';
                                        })()}
                                    </div>
                                </div>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                                    <div style={{ color:'#6b7280', fontSize:14 }}>Créée le</div>
                                    <div style={{ fontWeight:500, fontSize:14 }}>{selectedTableForQR.created_at ? new Date(selectedTableForQR.created_at).toLocaleDateString() : '-'}</div>
                                </div>
                                <div className="drawer-footer-actions">
                                    <button className="drawer-btn drawer-btn-primary" onClick={() => telechargerQRCode(selectedTableForQR)}>
                                        <Download size={16} style={{ marginRight:6 }} />Télécharger
                                    </button>
                                    <button className="drawer-btn drawer-btn-copy" onClick={() => copierQRCode(selectedTableForQR)}>
                                        <Copy size={16} style={{ marginRight:6 }} />Copier
                                    </button>
                                    <button className="drawer-btn drawer-btn-secondary" onClick={() => window.print()}>
                                        <span style={{ marginRight:6, display:'flex' }}><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="6" y="9" width="12" height="7" rx="2" /><path d="M6 17v2a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-2" /><path d="M6 9V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" /></svg></span>
                                        Imprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TablesNew;